import {ChangeDetectionStrategy, Component, inject, signal} from '@angular/core'
import { toSignal} from '@angular/core/rxjs-interop'
import { EventEmitter, Input, Output } from '@angular/core'
import { LongtailService, StudyService } from '@nx-superprep/backend/firebase'
import { AbstractControl, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { ResponsiveComponent } from '@nx-superprep/shared'
import { log } from '@nx-superprep/utils'
import { NgClass, NgTemplateOutlet } from '@angular/common'
import { map, merge, of, switchMap, take, takeUntil } from 'rxjs'
import { FloatLabelType, MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatButtonModule } from '@angular/material/button'
import { LegalComponent } from "../legal/legal.component";
import { NavigationBarComponent, NavigationBarState } from '../navigationbar/navigationbar.component'
import { FirebaseError } from '@angular/fire/app'
import { HttpStatusCode } from '@angular/common/http'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'

export interface JoinClassroomState {
  status:'join'|'open'|'succeed'|'failed'|undefined,
  error?: FirebaseError,
  classroomName?:string,
  teacherName?:string
}
export const isSuccessClassroom = (classroom:JoinClassroomState)=>classroom.status==='succeed'
export const isFailureClassroom = (classroom:JoinClassroomState)=>classroom.status==='failed'
export const isJoinClassroom = (classroom:JoinClassroomState)=>classroom.status==='join'
export const isOpenClassroom = (classroom:JoinClassroomState)=>classroom.status==='open'
export const isActiveClassroom = (classroom:JoinClassroomState)=>['join', 'open'].includes(classroom.status??'')
export const isResultClassroom = (classroom:JoinClassroomState)=>['succeed', 'failed'].includes(classroom.status??'')

@Component({
  selector: 'pop-ui-join-classroom',
  standalone: true,
  imports: [
    NgClass, NgTemplateOutlet,
    FormsModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatProgressSpinnerModule,
    LegalComponent, NavigationBarComponent,
  ],
  templateUrl: './join-classroom.component.html',
  styleUrl: './join-classroom.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JoinClassroomComponent extends ResponsiveComponent {
  @Input() classroom: JoinClassroomState = {status: 'join'}
  @Output() classroomChange = new EventEmitter<JoinClassroomState>()

  onClose(classroom: JoinClassroomState) {
    this.isOpenSlide = false
    this.classroomChange.emit(classroom)
  }

  title = 'join-classroom'

  private _isOpenSlide = false
  transientLabels = ['Checking Code', 'Joining Classroom']

  get isOpenSlide() {
    return this._isOpenSlide
  }
  set isOpenSlide(value: boolean) {
    this._isOpenSlide = value
  }

  private _subNavigationState: NavigationBarState = {
    current: { route: 'home', type: 'close-end' },
    title: '',
  }
  get subNavigationState() {
    return this._subNavigationState
  }
  set subNavigationState(value) {
    this._subNavigationState = value
    if (this._subNavigationState.action) { this.onClose(this.classroom) }
  }

  content:string|undefined = undefined

  get isOpenClassroom() {
    return isOpenClassroom(this.classroom)
  }

  get isPending() {
    return this.transientLabels.includes(this.submitLabel.toString())
  }

  onSubmit(FormGroup: FormGroup) {
    if (FormGroup.invalid || this.isPending) { return }

    const code = this.joinform.get('code')?.value

    if (!code) { return }

    if (this.classroom.status === 'open') {
      this.confirm(code)
    } else {
      this.checkCode(code)
    }
  }

  checkCode(code: string) {
    this.submitLabel.set('Checking Code')

    this.longtailService.validateClassroomCode(code)
    .pipe(take(1))
    .subscribe((result) => {
      if (result.statusCode !== HttpStatusCode.Ok) { // && !isDevMode()) { //! bypass logic for testing
        log.warn(result)
        this.code.setErrors({invalidCode: true})
        this.submitLabel.set(this.defaultLabel)
      } else {
        this.classroom.classroomName = result.classroomName ?? 'No Class Name'
        this.classroom.teacherName = result.teacherName ?? 'No Teacher Name'
        this.classroom.status = 'open'
        this.submitLabel.set('Confirm')
      }
    })
  }

  confirm(code:string) {
    this.submitLabel.set('Joining Classroom')

    this.longtailService.joinClassroom(code)
    .pipe(take(1))
    .subscribe(result => {
      if (result.statusCode !== HttpStatusCode.Ok) {
        this.code.setErrors({invalidCode: true})
        this.submitLabel.set(this.defaultLabel)
        this.onClose({...this.classroom, status: 'failed'})
      } else {
        this.studyService.refreshDayPlanAndModules()
        .pipe(take(1))
        .subscribe(() =>
          this.onClose({...this.classroom, status: 'succeed'})
        )
      }
    })
  }

  readonly code = new FormControl('', [Validators.required]);
  readonly hideRequiredControl = new FormControl(true);
  readonly floatLabelControl = new FormControl('auto' as FloatLabelType);

  readonly joinform = inject(FormBuilder).group({
    hideRequired: this.hideRequiredControl,
    floatLabel: this.floatLabelControl,
    code: this.code
  });
  protected readonly hideRequired = toSignal(
    this.hideRequiredControl.valueChanges.pipe(map(v => !!v)),
    {initialValue: true},
  );
  protected readonly floatLabel = toSignal(
    this.floatLabelControl.valueChanges.pipe(map(v => v || 'auto')),
    {initialValue: 'auto'},
  );

  errorMessage = signal('');

  updateErrorMessage() {
    if (this.code.hasError('required')) {
      this.errorMessage.set('Please enter a valid code');
    } else if (this.code.hasError('invalidCode')) {
      this.errorMessage.set('Please enter a valid code');
    } else {
      this.errorMessage.set('');
    }
  }

  defaultLabel = 'Join Class'
  submitLabel = signal(this.defaultLabel)

  codeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      return this.longtailService.validateClassroomCode(control.value)
      .pipe(switchMap((result) => {
        return of(result? null : {codeInvalid: true});
      }));
    };
  }

  onDisplay(value?: string) {
    switch (value) {
      case 'privacy': this.content = 'privacy'; break
      case 'terms': this.content = 'tacos'; break
      default: break
    }
    this.isOpenSlide = !!this.content
  }

  onLegalClose() {
    this.content = undefined
  }

  readonly studyService = inject(StudyService)
  readonly longtailService = inject(LongtailService)

  constructor(
  ) {
    super()
    super.rootClassName = 'join-classroom-root-class-name'
    merge(this.code.statusChanges, this.code.valueChanges)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => this.updateErrorMessage());
  }
}
